import {
  Button,
  Dialog,
  DialogContent,
  DialogFooter,
  DialogHeader,
  DialogTitle,
} from '@wundo/ui';
import { formatTime } from '@/components/agenda/meetingFormat.ts';
import { offlineRecordingClock } from './offlineRecordingClock.ts';
import { m } from '@/paraglide/messages.js';

export interface OfflineRecordingLiveDialogProps {
  open: boolean;
  /** Seconds so far, for the clock. */
  elapsed: number;
  /** When it began, or null when neither the server nor this device said. */
  startedAt: Date | null;
  /** The microphone the audio is coming from, as its driver names it. */
  device: string | null;
  /** A piece of audio was lost for good; the rest is still being recorded. */
  chunkLost: boolean;
  /** While the stop is in flight. */
  pending: boolean;
  onStop: () => void;
  /** Out of the way, down to the pill — the recording carries on. */
  onMinimize: () => void;
}

/**
 * The running recording, held in front of the page.
 *
 * A recording is not a setting that sits quietly in a corner: for as long as it
 * runs it is the thing the user is doing, and everything else on this page is
 * about a meeting that is happening in the room right now. So it takes the
 * screen, the way a call does, and the clock is what proves it is still moving.
 *
 * Minimising is the way out that keeps it running, and the only one — closing
 * this on the escape key means the same thing, because a keystroke may not end
 * a recording. Stopping is a button somebody has to reach for.
 *
 * The microphone is named and not changeable: switching devices mid-recording
 * means a second MediaRecorder session, whose chunks do not concatenate onto
 * the first one's.
 */
export function OfflineRecordingLiveDialog({
  open,
  elapsed,
  startedAt,
  device,
  chunkLost,
  pending,
  onStop,
  onMinimize,
}: OfflineRecordingLiveDialogProps) {
  return (
    <Dialog open={open} onOpenChange={(next) => !next && onMinimize()}>
      <DialogContent size='md'>
        <DialogHeader>
          <DialogTitle>{m.meeting_offline_recording_title()}</DialogTitle>
        </DialogHeader>

        <div className='flex flex-col gap-3.5 px-5 py-4'>
          <div>
            <p className='text-xl font-bold tabular-nums text-ink'>
              {offlineRecordingClock(elapsed)}
            </p>
            {startedAt && (
              <p className='mt-0.5 text-sm text-ink-muted'>
                {m.meeting_offline_recording_body({
                  time: formatTime(startedAt.toISOString()),
                })}
              </p>
            )}
          </div>

          <div>
            {device && (
              <p className='text-sm text-ink-body'>
                {m.meeting_offline_recording_device({ name: device })}
              </p>
            )}
            <p className='mt-0.5 text-sm text-ink-muted'>
              {m.meeting_offline_recording_device_note()}
            </p>
          </div>

          <p className='text-sm text-ink-body'>{m.meeting_offline_dialog_consent()}</p>

          {chunkLost && (
            <p role='alert' className='text-sm text-danger'>
              {m.meeting_offline_chunk_lost()}
            </p>
          )}
        </div>

        <DialogFooter>
          <Button size='sm' variant='quiet' onClick={onMinimize}>
            {m.meeting_offline_minimize()}
          </Button>
          <Button size='sm' variant='danger' disabled={pending} onClick={onStop}>
            {m.meeting_offline_stop()}
          </Button>
        </DialogFooter>
      </DialogContent>
    </Dialog>
  );
}
